import { clsx } from 'clsx';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { errorMessage } from '../../lib/api';

type ToastKind = 'success' | 'error' | 'info';

type ToastItem = { id: number; kind: ToastKind; message: string };

export type ToastApi = {
  success: (message: string) => void;
  /** Accepts a message or any thrown value; `ApiError`s show the server's message. */
  error: (messageOrError: unknown) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DURATION: Record<ToastKind, number> = { success: 4000, info: 5000, error: 8000 };
const MAX_VISIBLE = 4;

const KIND_STYLE: Record<ToastKind, { icon: typeof Info; iconClass: string }> = {
  success: { icon: CircleCheck, iconClass: 'text-emerald-500' },
  error: { icon: CircleAlert, iconClass: 'text-red-500' },
  info: { icon: Info, iconClass: 'text-sky-500' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = nextId.current++;
    setToasts((list) => [...list.slice(-(MAX_VISIBLE - 1)), { id, kind, message }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (value) => push('error', typeof value === 'string' ? value : errorMessage(value)),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-relevant="additions"
          className="pointer-events-none fixed right-4 bottom-4 z-[80] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
        >
          {toasts.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const { icon: Icon, iconClass } = KIND_STYLE[toast.kind];

  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), DURATION[toast.kind]);
    return () => window.clearTimeout(timer);
  }, [paused, toast.id, toast.kind, onDismiss]);

  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="pointer-events-auto flex animate-pop-in items-start gap-2.5 rounded-lg border border-slate-200 bg-surface px-3 py-2.5 text-[13px] text-slate-800 shadow-popover"
    >
      <Icon size={16} aria-hidden className={clsx('mt-0.5 shrink-0', iconClass)} />
      <p className="flex-1 leading-5 break-words">{toast.message}</p>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        className="-mr-1 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

/** `const toast = useToast(); toast.success('Lead saved'); toast.error(err);` */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
